import { t } from '../../i18n/init.js';
import type { WalkSnapshot } from '../../walk/types.js';
import type { ComponentInstance } from '../component.js';
import { setText } from '../dom-helpers.js';

export interface WalkHudProps {
  snapshot: WalkSnapshot;
  embed: boolean;
  // WS5 PR3.3a: nome della viewbox corrente (es. "Soggiorno") da mostrare
  // nel top pill tra il tag "walk mode" e l'altezza. null = legacy/no viewbox
  // (pill come prima: "walk mode · 1.65m"). Il caller (renderer) deriva
  // questo da viewboxSnapshot.available[currentArrayIdx].name.
  viewboxName?: string | null;
  // WS5 PR4a (Codex add#1): touch mode → bottom overlay mostra una hint
  // testuale (joystick + drag + pinch) invece della grid WASD desktop.
  touchMode?: boolean;
}

interface KeyHint {
  key: string;
  label: string;
}

const KBD_BASE =
  'background:rgba(255,255,255,0.10);border:1px solid rgba(255,255,255,0.15);border-radius:0.25rem;padding:0 0.375rem;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:0.6875rem;color:#f4f4f5';

function makeKbd(content: string, sizeBig = false): HTMLElement {
  const el = document.createElement('kbd');
  el.style.cssText = sizeBig
    ? `${KBD_BASE};display:flex;align-items:center;justify-content:center;width:1.75rem;height:1.75rem;font-size:0.6875rem;font-weight:700`
    : KBD_BASE;
  el.textContent = content;
  return el;
}

export function mountWalkHud(
  root: HTMLElement,
  initial: WalkHudProps,
): ComponentInstance<WalkHudProps> {
  const wrap = document.createElement('div');
  wrap.classList.add('viewer-ui-interactive');
  wrap.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:11';
  wrap.style.display = 'none';

  // Top-center pill: "walk mode · altezza Xm"
  const topBadge = document.createElement('div');
  topBadge.setAttribute('role', 'status');
  topBadge.setAttribute('aria-live', 'polite');
  topBadge.style.cssText =
    'position:fixed;top:1rem;left:50%;transform:translateX(-50%);background:rgba(15,15,17,0.85);backdrop-filter:blur(8px);border:1px solid rgba(96,165,250,0.30);padding:0.375rem 0.875rem;border-radius:9999px;display:flex;align-items:center;gap:0.5rem;font-family:system-ui,sans-serif;font-size:0.6875rem;color:#bfdbfe;pointer-events:none';
  const dot = document.createElement('span');
  dot.setAttribute('aria-hidden', 'true');
  dot.style.cssText =
    'display:inline-block;width:0.375rem;height:0.375rem;border-radius:9999px;background:#60a5fa';
  topBadge.appendChild(dot);
  const labelTag = document.createElement('span');
  labelTag.style.cssText =
    'text-transform:uppercase;letter-spacing:0.05em;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;color:#93c5fd';
  labelTag.textContent = t('viewer.walk.tag');
  topBadge.appendChild(labelTag);
  const sep1 = document.createElement('span');
  sep1.setAttribute('aria-hidden', 'true');
  sep1.style.cssText = 'color:#71717a';
  sep1.textContent = '·';
  topBadge.appendChild(sep1);
  // WS5 PR3.3a: optional viewbox name pill (es. "Soggiorno"). Visible solo
  // se viewboxName !== null/undefined. Separatore dopo, controllato in apply().
  const viewboxNameLabel = document.createElement('span');
  viewboxNameLabel.style.cssText =
    'color:#bfdbfe;font-family:system-ui,sans-serif;font-weight:600';
  viewboxNameLabel.style.display = 'none';
  topBadge.appendChild(viewboxNameLabel);
  const sepViewbox = document.createElement('span');
  sepViewbox.setAttribute('aria-hidden', 'true');
  sepViewbox.style.cssText = 'color:#71717a';
  sepViewbox.textContent = '·';
  sepViewbox.style.display = 'none';
  topBadge.appendChild(sepViewbox);
  const heightLabel = document.createElement('span');
  heightLabel.style.cssText =
    'font-family:ui-monospace,SFMono-Regular,Menlo,monospace;color:#a1a1aa';
  topBadge.appendChild(heightLabel);
  wrap.appendChild(topBadge);

  // Bottom-center WASD overlay
  const bottomOverlay = document.createElement('section');
  bottomOverlay.setAttribute('role', 'region');
  bottomOverlay.setAttribute('aria-label', t('viewer.walk.controls_label'));
  bottomOverlay.style.cssText = initial.embed
    ? 'position:fixed;bottom:1rem;left:50%;transform:translateX(-50%);background:rgba(15,15,17,0.85);backdrop-filter:blur(8px);border:1px solid rgba(255,255,255,0.10);border-radius:1rem;padding:0.625rem 1rem;display:flex;align-items:center;gap:0.75rem;font-family:system-ui,sans-serif;font-size:0.6875rem;color:#d4d4d8;pointer-events:none'
    : 'position:fixed;bottom:3rem;left:50%;transform:translateX(-50%);background:rgba(15,15,17,0.85);backdrop-filter:blur(8px);border:1px solid rgba(255,255,255,0.10);border-radius:1rem;padding:0.875rem 1.25rem;display:flex;align-items:center;gap:1rem;font-family:system-ui,sans-serif;font-size:0.75rem;color:#d4d4d8;pointer-events:none';

  // WASD grid (W on top, A S D below)
  const wasdGrid = document.createElement('div');
  wasdGrid.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:0.25rem';
  wasdGrid.appendChild(makeKbd('W', true));
  const asdRow = document.createElement('div');
  asdRow.style.cssText = 'display:flex;gap:0.25rem';
  asdRow.appendChild(makeKbd('A', true));
  asdRow.appendChild(makeKbd('S', true));
  asdRow.appendChild(makeKbd('D', true));
  wasdGrid.appendChild(asdRow);
  bottomOverlay.appendChild(wasdGrid);

  const sep2 = document.createElement('span');
  sep2.setAttribute('aria-hidden', 'true');
  sep2.style.cssText = 'width:1px;height:2.5rem;background:rgba(255,255,255,0.10)';
  bottomOverlay.appendChild(sep2);

  const moveLabel = document.createElement('span');
  moveLabel.style.cssText = 'color:#a1a1aa';
  moveLabel.textContent = t('viewer.walk.move');
  bottomOverlay.appendChild(moveLabel);

  const sep3 = sep2.cloneNode() as HTMLElement;
  bottomOverlay.appendChild(sep3);

  const inlineHints: readonly KeyHint[] = [
    { key: 'Mouse', label: t('viewer.walk.look_around') },
    { key: 'Space', label: t('viewer.walk.jump') },
    // Inc C: corsa (Shift) + accovacciati (C).
    { key: 'Shift', label: t('viewer.walk.run') },
    { key: 'C', label: t('viewer.walk.crouch') },
    { key: 'Esc', label: t('viewer.walk.exit') },
  ];
  inlineHints.forEach((hint, idx) => {
    const span = document.createElement('span');
    span.style.cssText = 'display:inline-flex;align-items:center;gap:0.375rem';
    span.appendChild(makeKbd(hint.key));
    const text = document.createElement('span');
    text.style.cssText = 'color:#a1a1aa';
    text.textContent = hint.label;
    span.appendChild(text);
    bottomOverlay.appendChild(span);
    if (idx < inlineHints.length - 1) {
      const sep = sep2.cloneNode() as HTMLElement;
      bottomOverlay.appendChild(sep);
    }
  });
  wrap.appendChild(bottomOverlay);

  // WS5 PR4a — bottom hint testuale per modalita' touch (mobile FP).
  // Mostrata al posto del WASD overlay quando touchMode=true.
  const touchOverlay = document.createElement('section');
  touchOverlay.setAttribute('role', 'region');
  touchOverlay.setAttribute('aria-label', t('viewer.walk.controls_label'));
  touchOverlay.style.cssText = initial.embed
    ? 'position:fixed;bottom:0.75rem;left:50%;transform:translateX(-50%);background:rgba(15,15,17,0.85);backdrop-filter:blur(8px);border:1px solid rgba(255,255,255,0.10);border-radius:9999px;padding:0.375rem 0.875rem;font-family:system-ui,sans-serif;font-size:0.6875rem;color:#d4d4d8;pointer-events:none;text-align:center;max-width:88vw'
    : 'position:fixed;bottom:1.5rem;left:50%;transform:translateX(-50%);background:rgba(15,15,17,0.85);backdrop-filter:blur(8px);border:1px solid rgba(255,255,255,0.10);border-radius:9999px;padding:0.5rem 1rem;font-family:system-ui,sans-serif;font-size:0.75rem;color:#d4d4d8;pointer-events:none;text-align:center;max-width:90vw';
  touchOverlay.textContent = t('viewer.walk.touch_hint');
  touchOverlay.style.display = 'none';
  wrap.appendChild(touchOverlay);

  // Crosshair center (decorative). Su touch lo nascondiamo (mira via dito).
  const crosshair = document.createElement('div');
  crosshair.setAttribute('aria-hidden', 'true');
  crosshair.style.cssText =
    'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);width:0.5rem;height:0.5rem;border-radius:9999px;border:1px solid rgba(255,255,255,0.50);pointer-events:none';
  wrap.appendChild(crosshair);

  root.appendChild(wrap);

  const apply = (props: WalkHudProps): void => {
    wrap.style.display = props.snapshot.active ? 'block' : 'none';
    if (!props.snapshot.active) return;
    const isTouch = props.touchMode === true;
    bottomOverlay.style.display = isTouch ? 'none' : '';
    touchOverlay.style.display = isTouch ? '' : 'none';
    crosshair.style.display = isTouch ? 'none' : '';
    const formattedHeight = t('viewer.walk.height', {
      height: (Math.round(props.snapshot.eyeHeight * 100) / 100).toFixed(2),
    });
    setText(heightLabel, formattedHeight);
    // WS5 PR3.3a: viewbox name pill toggle. Mostrato solo se props.viewboxName
    // e' una stringa non vuota. Caller (renderer) passa null per scena legacy
    // o viewbox.currentArrayIdx===null.
    const vbName = typeof props.viewboxName === 'string' ? props.viewboxName.trim() : '';
    if (vbName.length > 0) {
      setText(viewboxNameLabel, vbName);
      viewboxNameLabel.style.display = '';
      sepViewbox.style.display = '';
    } else {
      viewboxNameLabel.style.display = 'none';
      sepViewbox.style.display = 'none';
    }
  };

  apply(initial);

  return {
    update(props: WalkHudProps) {
      apply(props);
    },
    cleanup() {
      wrap.remove();
    },
  };
}
